import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown } from "lucide-react";
import { getAdminTeams, saveScope } from "@/lib/api";
import {
  queryClient,
  removeAllScopedFromCache,
  unscopedKey,
} from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import type { AdminTeamRow, MePayload, MeTeam } from "@/lib/types";

type SwitcherOption = {
  id: string;
  name: string;
  membership: boolean;
  role?: string;
};

/** Header team switcher.
 *
 * Visible only when the signed-in user has 2+ team memberships, or is
 * a global admin. Posts `PUT /api/me/scope` with the selected team_id;
 * the backend resolves the season (no UI for season per PR #60).
 * Reconciles auth + scope stores and evicts every scoped cache entry
 * so subsequent reads issue against the new team.
 */
export function TeamSwitcher() {
  const me = useAuthStore((s) => s.me);
  const activeTeam = useScopeStore((s) => s.active?.team ?? null);
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  const applyAuth = useAuthStore((s) => s.applyMePayload);
  const applyScope = useScopeStore((s) => s.applyMePayload);

  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isGlobalAdmin = !!me?.user?.is_global_admin;
  const memberships = me?.teams ?? [];
  const adminTeamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams-switcher"),
    queryFn: getAdminTeams,
    enabled: open && isGlobalAdmin,
    staleTime: 60_000,
  });

  const options = useMemo<SwitcherOption[]>(() => {
    const ownTeams: SwitcherOption[] = memberships.map((t: MeTeam) => ({
      id: t.id,
      name: t.name,
      membership: true,
      role: t.membership_role,
    }));
    if (!isGlobalAdmin) return ownTeams;
    const ownIds = new Set(ownTeams.map((o) => o.id));
    const adminTeams: SwitcherOption[] = (adminTeamsQuery.data ?? [])
      .filter((t: AdminTeamRow) => !ownIds.has(t.id))
      .map((t) => ({ id: t.id, name: t.name, membership: false }));
    return [...ownTeams, ...adminTeams];
  }, [memberships, isGlobalAdmin, adminTeamsQuery.data]);

  // Render-nothing rules.
  const visible = !!me?.user && (memberships.length >= 2 || isGlobalAdmin);

  // Close on outside click, Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const el = containerRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!visible) return null;

  const triggerLabel = selectionRequired || !activeTeam
    ? "Select team"
    : activeTeam.name;

  const onSelect = async (teamId: string) => {
    if (pendingId) return;
    if (activeTeam?.id === teamId) {
      setOpen(false);
      return;
    }
    setError(null);
    setPendingId(teamId);
    try {
      const payload: MePayload = await saveScope({ team_id: teamId });
      applyAuth(payload);
      applyScope(payload);
      queryClient.setQueryData(unscopedKey("me"), payload);
      removeAllScopedFromCache();
      setOpen(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not switch team";
      setError(msg);
    } finally {
      setPendingId(null);
    }
  };

  const membershipOptions = options.filter((o) => o.membership);
  const adminOnlyOptions = options.filter((o) => !o.membership);

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="header-team-switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex max-w-full items-center gap-1.5 rounded border border-accent bg-accent px-2.5 py-1 font-medium text-accent-ink transition-colors hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <span className="min-w-0 truncate font-display uppercase tracking-wider text-[0.68rem]">
          {triggerLabel}
        </span>
        <ChevronDown
          size={13}
          strokeWidth={2.2}
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          role="listbox"
          aria-label="Switch team"
          data-testid="header-team-switcher-panel"
          className="absolute right-0 z-50 mt-1 max-h-[min(60vh,28rem)] w-[min(16rem,calc(100vw-2rem))] overflow-y-auto rounded border border-rule bg-surface shadow-lg"
        >
          {membershipOptions.length > 0 ? (
            <div data-testid="header-team-switcher-group-memberships">
              {isGlobalAdmin && adminOnlyOptions.length > 0 ? (
                <div className="px-3 pt-2 pb-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Your teams
                </div>
              ) : null}
              {membershipOptions.map((o) => (
                <OptionRow
                  key={o.id}
                  option={o}
                  active={o.id === activeTeam?.id}
                  pending={o.id === pendingId}
                  onSelect={onSelect}
                />
              ))}
            </div>
          ) : null}

          {isGlobalAdmin && adminOnlyOptions.length > 0 ? (
            <div data-testid="header-team-switcher-group-admin">
              <div className="px-3 pt-2 pb-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                {membershipOptions.length > 0 ? "All teams" : "Teams"}
              </div>
              {adminOnlyOptions.map((o) => (
                <OptionRow
                  key={o.id}
                  option={o}
                  active={o.id === activeTeam?.id}
                  pending={o.id === pendingId}
                  onSelect={onSelect}
                />
              ))}
            </div>
          ) : null}

          {isGlobalAdmin && adminTeamsQuery.isLoading ? (
            <div className="px-3 py-2 text-xs text-ink-muted">Loading teams…</div>
          ) : null}

          {options.length === 0 && !adminTeamsQuery.isLoading ? (
            isGlobalAdmin ? (
              <div className="px-3 py-2 text-xs text-ink-muted">
                No teams yet.{" "}
                <a href="/admin/teams" className="underline">
                  Create one
                </a>
                .
              </div>
            ) : (
              <div className="px-3 py-2 text-xs text-ink-muted">No teams available.</div>
            )
          ) : null}

          {error ? (
            <div
              data-testid="header-team-switcher-error"
              className="border-t border-rule px-3 py-2 text-xs text-destructive"
            >
              {error}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function OptionRow({
  option,
  active,
  pending,
  onSelect,
}: {
  option: SwitcherOption;
  active: boolean;
  pending: boolean;
  onSelect: (id: string) => void | Promise<void>;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      disabled={pending}
      onClick={() => void onSelect(option.id)}
      data-testid={`header-team-switcher-option-${option.id}`}
      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-field disabled:opacity-50 ${
        active ? "bg-accent text-accent-ink" : ""
      }`}
    >
      <span className={active ? "text-accent-ink" : "text-ink"}>
        {option.name}
      </span>
      <span
        className={`flex items-center gap-2 text-[0.6rem] font-display uppercase tracking-wider ${
          active ? "text-accent-ink/80" : "text-ink-muted"
        }`}
      >
        {option.role ? <span>{option.role.replace(/_/g, " ")}</span> : null}
        {active ? (
          <>
            <Check size={13} strokeWidth={2.4} aria-hidden="true" />
            <span className="sr-only">active</span>
          </>
        ) : null}
        {pending ? <span>…</span> : null}
      </span>
    </button>
  );
}
